import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { formatNumber } from '../utils/dashboardTranslations';

const initialForm = {
    governorate: '',
    city: '',
    deliveryFee: '',
    isActive: true,
    centerLatitude: '',
    centerLongitude: '',
    radiusKm: '1.5',
};

function DeliveryZones() {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [zones, setZones] = useState([]);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [filterGovernorate, setFilterGovernorate] = useState('');
    const [governorates, setGovernorates] = useState([]);

    useEffect(() => {
        fetchZones();
        fetchGovernorates();
    }, []);

    const fetchZones = async (govFilter) => {
        try {
            setLoading(true);
            const params = {};
            if (govFilter) params.governorate = govFilter;
            const response = await api.get('/delivery-zones', { params });
            const data = Array.isArray(response.data) ? response.data : (response.data?.items || []);
            setZones(data);
        } catch (error) {
            console.error('Error loading zones:', error);
            alert('فشل تحميل مناطق التوصيل');
        } finally {
            setLoading(false);
        }
    };

    const fetchGovernorates = async () => {
        try {
            const response = await api.get('/delivery-zones/governorates');
            const data = Array.isArray(response.data) ? response.data : [];
            setGovernorates(data);
        } catch (error) {
            console.error('Error loading governorates:', error);
        }
    };

    const handleFilterChange = (gov) => {
        setFilterGovernorate(gov);
        fetchZones(gov || undefined);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.governorate.trim()) {
            alert('المحافظة مطلوبة');
            return;
        }
        if (form.deliveryFee === '' || Number(form.deliveryFee) < 0) {
            alert('رسم التوصيل يجب أن يكون 0 أو أكثر');
            return;
        }

        try {
            setSaving(true);
            const payload = {
                governorate: form.governorate.trim(),
                city: form.city.trim() || null,
                deliveryFee: Number(form.deliveryFee),
                isActive: !!form.isActive,
                centerLatitude: form.centerLatitude !== '' ? parseFloat(form.centerLatitude) : null,
                centerLongitude: form.centerLongitude !== '' ? parseFloat(form.centerLongitude) : null,
                radiusKm: form.radiusKm !== '' ? parseFloat(form.radiusKm) : null,
            };

            if (editId) {
                await api.put(`/delivery-zones/${editId}`, payload);
                alert('تم تحديث منطقة التوصيل بنجاح');
            } else {
                await api.post('/delivery-zones', payload);
                alert('تم إنشاء منطقة التوصيل بنجاح');
            }
            resetForm();
            fetchZones(filterGovernorate || undefined);
            fetchGovernorates();
        } catch (error) {
            console.error('Error saving zone:', error);
            const msg = error?.response?.data;
            alert(typeof msg === 'string' ? msg : 'فشل حفظ منطقة التوصيل');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (zone) => {
        setEditId(zone.id);
        setForm({
            governorate: zone.governorate || '',
            city: zone.city || '',
            deliveryFee: zone.deliveryFee ?? '',
            isActive: zone.isActive !== false,
            centerLatitude: zone.centerLatitude ?? '',
            centerLongitude: zone.centerLongitude ?? '',
            radiusKm: zone.radiusKm ?? '1.5',
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (zoneId) => {
        if (!window.confirm('هل تريد حذف منطقة التوصيل هذه؟')) return;
        try {
            await api.delete(`/delivery-zones/${zoneId}`);
            fetchZones(filterGovernorate || undefined);
            fetchGovernorates();
        } catch (error) {
            console.error('Error deleting zone:', error);
            alert('فشل حذف منطقة التوصيل');
        }
    };

    const handleToggleActive = async (zone) => {
        try {
            await api.put(`/delivery-zones/${zone.id}`, {
                isActive: !zone.isActive,
            });
            fetchZones(filterGovernorate || undefined);
        } catch (error) {
            console.error('Error toggling zone:', error);
            alert('فشل تحديث الحالة');
        }
    };

    const resetForm = () => {
        setForm(initialForm);
        setEditId(null);
    };

    const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

    const getGoogleMapsErrorMessage = (data) => {
        const status = data?.status || 'UNKNOWN_ERROR';
        const errorMessage = data?.error_message ? `: ${data.error_message}` : '';

        switch (status) {
            case 'ZERO_RESULTS':
                return 'لم يتم العثور على نتائج لهذه الإحداثيات';
            case 'REQUEST_DENIED':
                return `تم رفض طلب خرائط جوجل (${status})${errorMessage}`;
            case 'OVER_QUERY_LIMIT':
                return `تم تجاوز حد طلبات خرائط جوجل (${status})${errorMessage}`;
            case 'INVALID_REQUEST':
                return `طلب خرائط جوجل غير صالح (${status})${errorMessage}`;
            case 'UNKNOWN_ERROR':
                return `حدث خطأ مؤقت من خرائط جوجل (${status})${errorMessage}`;
            default:
                return `فشل جلب بيانات الموقع من خرائط جوجل (${status})${errorMessage}`;
        }
    };

    const fetchFromGoogleMaps = async () => {
        const lat = parseFloat(form.centerLatitude);
        const lng = parseFloat(form.centerLongitude);
        if (isNaN(lat) || isNaN(lng)) {
            alert('أدخل إحداثيات صحيحة أولاً');
            return;
        }
        if (!GOOGLE_API_KEY) {
            alert('مفتاح خرائط جوجل غير مضبوط في إعدادات الواجهة');
            return;
        }
        try {
            const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_API_KEY}&language=ar`;
            const res = await fetch(url);
            const data = await res.json();
            if (data.status !== 'OK') {
                console.error('Google Maps geocode failed:', data);
                alert(getGoogleMapsErrorMessage(data));
                return;
            }
            if (!data.results.length) {
                alert('لم يتم العثور على نتائج لهذه الإحداثيات');
                return;
            }
            const components = data.results[0].address_components;
            const get = (type) => components.find(c => c.types.includes(type))?.long_name || '';
            const governorate = get('administrative_area_level_1') || get('administrative_area_level_2');
            const city = get('sublocality_level_1') || get('sublocality') || get('locality') || get('administrative_area_level_2');
            setForm(p => ({ ...p, governorate, city }));
        } catch (e) {
            console.error('Google Maps request failed:', e);
            alert('خطأ في الاتصال بخرائط جوجل');
        }
    };

    return (
        <div>
            <h4 className="mb-3">🗺️ إدارة مناطق التوصيل</h4>

            {/* Form Card */}
            <div className="card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>{editId ? 'تعديل منطقة توصيل' : 'إضافة منطقة توصيل جديدة'}</strong>
                    {editId && (
                        <button className="btn btn-outline-secondary btn-sm" onClick={resetForm}>
                            إلغاء التعديل
                        </button>
                    )}
                </div>
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label">المحافظة *</label>
                                <input
                                    className="form-control"
                                    value={form.governorate}
                                    onChange={(e) => setForm((p) => ({ ...p, governorate: e.target.value }))}
                                    placeholder="مثال: دمشق"
                                    required
                                />
                            </div>

                            <div className="col-md-4">
                                <label className="form-label">المدينة / المنطقة</label>
                                <input
                                    className="form-control"
                                    value={form.city}
                                    onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                                    placeholder="اتركه فارغاً للرسم الافتراضي للمحافظة"
                                />
                                <small className="text-muted">اتركه فارغاً لتحديد الرسم الافتراضي لكامل المحافظة</small>
                            </div>

                            <div className="col-md-4">
                                <label className="form-label">رسم التوصيل (ل.س) *</label>
                                <input
                                    type="number"
                                    min="0"
                                    className="form-control"
                                    value={form.deliveryFee}
                                    onChange={(e) => setForm((p) => ({ ...p, deliveryFee: e.target.value }))}
                                    placeholder="مثال: 5000"
                                    required
                                />
                            </div>

                            <div className="col-md-4 d-flex align-items-end">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={form.isActive}
                                        onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                                        id="isActiveSwitch"
                                    />
                                    <label className="form-check-label" htmlFor="isActiveSwitch">
                                        مفعّلة
                                    </label>
                                </div>
                            </div>

                            {/* Coordinates section */}
                            <div className="col-12">
                                <hr className="my-2" />
                                <small className="text-muted d-block mb-2">
                                    📍 الإحداثيات (اختياري) — أدخل المركز الجغرافي للمنطقة
                                </small>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">خط العرض</label>
                                <input
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    value={form.centerLatitude}
                                    onChange={(e) => setForm(p => ({ ...p, centerLatitude: e.target.value }))}
                                    placeholder="مثال: 32.625"
                                />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label">خط الطول</label>
                                <input
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    value={form.centerLongitude}
                                    onChange={(e) => setForm(p => ({ ...p, centerLongitude: e.target.value }))}
                                    placeholder="مثال: 36.098"
                                />
                            </div>

                            <div className="col-md-2">
                                <label className="form-label">نطاق التوصيل (كم)</label>
                                <input
                                    type="number"
                                    min="0.1"
                                    step="0.1"
                                    className="form-control"
                                    value={form.radiusKm}
                                    onChange={(e) => setForm(p => ({ ...p, radiusKm: e.target.value }))}
                                    placeholder="مثال: 1.5"
                                />
                            </div>

                            <div className="col-md-4 d-flex align-items-end">
                                <button
                                    type="button"
                                    className="btn btn-outline-success w-100"
                                    onClick={fetchFromGoogleMaps}
                                >
                                    🗺️ جلب الاسم من خرائط جوجل
                                </button>
                            </div>

                            <div className="col-12">
                                <button type="submit" className="btn btn-primary" disabled={saving}>
                                    {saving ? 'جاري الحفظ...' : editId ? 'تحديث' : 'إضافة'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* List Card */}
            <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <strong>مناطق التوصيل ({zones.length})</strong>
                    <div className="d-flex gap-2 align-items-center">
                        <label className="mb-0 text-muted" style={{ fontSize: '14px' }}>تصفية بالمحافظة:</label>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto', minWidth: '150px' }}
                            value={filterGovernorate}
                            onChange={(e) => handleFilterChange(e.target.value)}
                        >
                            <option value="">الكل</option>
                            {governorates.map((gov, i) => (
                                <option key={i} value={gov}>{gov}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center p-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">جاري التحميل...</span>
                            </div>
                        </div>
                    ) : zones.length === 0 ? (
                        <div className="text-center text-muted p-4">
                            لا توجد مناطق توصيل مضافة بعد
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>المحافظة</th>
                                        <th>المدينة / المنطقة</th>
                                        <th>رسم التوصيل</th>
                                        <th>الحالة</th>
                                        <th>إحداثيات</th>
                                        <th>الإجراءات</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {zones.map((zone, index) => (
                                        <tr key={zone.id}>
                                            <td>{index + 1}</td>
                                            <td><strong>{zone.governorate}</strong></td>
                                            <td>
                                                {zone.city ? (
                                                    zone.city
                                                ) : (
                                                    <span className="badge bg-info text-dark">افتراضي للمحافظة</span>
                                                )}
                                            </td>
                                            <td>
                                                <strong>{formatNumber(zone.deliveryFee)}</strong> ل.س
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge ${zone.isActive ? 'bg-success' : 'bg-secondary'}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleToggleActive(zone)}
                                                    title="انقر لتغيير الحالة"
                                                >
                                                    {zone.isActive ? 'مفعّلة' : 'معطّلة'}
                                                </span>
                                            </td>
                                            <td>
                                                {zone.centerLatitude ? (
                                                    <span className="badge bg-success">✓</span>
                                                ) : (
                                                    <span className="badge bg-secondary">—</span>
                                                )}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-outline-primary btn-sm me-1"
                                                    onClick={() => handleEdit(zone)}
                                                >
                                                    ✏️ تعديل
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={() => handleDelete(zone.id)}
                                                >
                                                    🗑️ حذف
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Help section */}
            <div className="card mt-4">
                <div className="card-header">
                    <strong>💡 كيفية الاستخدام</strong>
                </div>
                <div className="card-body">
                    <ul className="mb-0" style={{ lineHeight: '2' }}>
                        <li>أضف <strong>المحافظة فقط</strong> (بدون مدينة) لتحديد رسم التوصيل الافتراضي لكامل المحافظة</li>
                        <li>أضف <strong>المحافظة + المدينة</strong> لتحديد رسم توصيل خاص بمنطقة معينة (يتجاوز الرسم الافتراضي)</li>
                        <li>مثال: "دمشق" = 5000 ل.س (افتراضي)، "دمشق + المزة" = 3000 ل.س (خاص)</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default DeliveryZones;
